import { errorInterceptor } from '@nutrix/error-interceptor';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '@nutrix/data-access-auth';
import { ApplicationConfig, ErrorHandler, importProvidersFrom, provideAppInitializer, inject } from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  Router,
  withComponentInputBinding,
  withPreloading,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';
import { appRoutes } from './app.routes';
import { JwtModule } from '@auth0/angular-jwt';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { APP_CONFIG } from '@nutrix/util-config';
import { environment } from '../environments/environment';
import { authInterceptor, tenantInterceptor } from '@nutrix/auth-interceptor';
import { ConfirmationService, MessageService } from 'primeng/api';
import { LOCALE_ID } from '@angular/core';
import localeEs from '@angular/common/locales/es-CL';
import { registerLocaleData } from '@angular/common';
import * as echarts from 'echarts/core';
import * as Sentry from '@sentry/angular';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { provideEchartsCore } from 'ngx-echarts';
import { definePreset } from '@primeng/themes';

const MyPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#fff0f2',
      100: '#ffdde2',
      200: '#ffc0ca',
      300: '#ff94a5',
      400: '#ff5772',
      500: '#ff2346',
      600: '#ff0029',
      700: '#d70023',
      800: '#b1031f',
      900: '#920a20',
      950: '#50000d',
    },
  },
});

const langES = {
  time: {
    month: [
      'enero',
      'febrero',
      'marzo',
      'abril',
      'mayo',
      'junio',
      'julio',
      'agosto',
      'septiembre',
      'octubre',
      'noviembre',
      'diciembre',
    ],
    monthAbbr: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
    dayOfWeek: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
    dayOfWeekAbbr: ['dom', 'lun', 'mar', 'mie', 'jue', 'vie', 'sáb'],
  },
  legend: {
    selector: {
      all: 'Todas',
      inverse: 'Inversa',
    },
  },
  toolbox: {
    brush: {
      title: {
        rect: 'Selección de cuadro',
        polygon: 'Selección de lazo',
        lineX: 'Seleccionar horizontalmente',
        lineY: 'Seleccionar verticalmente',
        keep: 'Mantener selección',
        clear: 'Despejar selecciones',
      },
    },
    dataView: {
      title: 'Ver datos',
      lang: ['Ver datos', 'Cerrar', 'Actualizar'],
    },
    dataZoom: {
      title: {
        zoom: 'Zoom',
        back: 'Restablecer zoom',
      },
    },
    magicType: {
      title: {
        line: 'Cambiar a gráfico de líneas',
        bar: 'Cambiar a gráfico de barras',
        stack: 'Pila',
        tiled: 'Teja',
      },
    },
    restore: {
      title: 'Restaurar',
    },
    saveAsImage: {
      title: 'Guardar como imagen',
      lang: ['Clic derecho para guardar imagen'],
    },
  },
  series: {
    typeNames: {
      pie: 'Gráfico circular',
      bar: 'Gráfico de barras',
      line: 'Gráfico de líneas',
      scatter: 'Diagrama de dispersión',
      effectScatter: 'Diagrama de dispersión de ondas',
      radar: 'Gráfico de radar',
      tree: 'Árbol',
      treemap: 'Mapa de árbol',
      boxplot: 'Diagrama de caja',
      candlestick: 'Gráfico de velas',
      k: 'Gráfico de líneas K',
      heatmap: 'Mapa de calor',
      map: 'Mapa',
      parallel: 'Mapa de coordenadas paralelas',
      lines: 'Gráfico de líneas',
      graph: 'Gráfico de relaciones',
      sankey: 'Diagrama de Sankey',
      funnel: 'Gráfico de embudo',
      gauge: 'Medidor',
      pictorialBar: 'Gráfico de barras pictóricas',
      themeRiver: 'Mapa de río temático',
      sunburst: 'Sunburst',
      custom: 'Gráfico personalizado',
      chart: 'Gráfico',
    },
  },
  aria: {
    general: {
      withTitle: 'Este es un gráfico sobre "{title}"',
      withoutTitle: 'Este es un gráfico',
    },
    series: {
      single: {
        prefix: '',
        withName: ' con tipo {seriesType} llamado {seriesName}.',
        withoutName: ' con tipo {seriesType}.',
      },
      multiple: {
        prefix: '. Consta de {seriesCount} series.',
        withName: ' La serie {seriesId} es un {seriesType} que representa {seriesName}.',
        withoutName: ' La serie {seriesId} es un {seriesType}.',
        separator: {
          middle: '',
          end: '',
        },
      },
    },
    data: {
      allData: 'Los datos son los siguientes: ',
      partialData: 'Los primeros {displayCnt} elementos son: ',
      withName: 'los datos para {name} son {value}',
      withoutName: '{value}',
      separator: {
        middle: ', ',
        end: '. ',
      },
    },
  },
};

echarts.registerLocale('ES', langES);
registerLocaleData(localeEs);

export function tokenGetter() {
  return null;
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: APP_CONFIG, useValue: environment },
    { provide: LOCALE_ID, useValue: 'es-CL' },
    provideRouter(
      appRoutes,
      withPreloading(PreloadAllModules),
      withComponentInputBinding(),
      withViewTransitions(),
      withRouterConfig({ resolveNavigationPromiseOnError: true }),
    ),
    importProvidersFrom(
      JwtModule.forRoot({
        config: {
          tokenGetter: tokenGetter,
          allowedDomains: ['localhost:4200'],
          disallowedRoutes: [],
        },
      }),
    ),

    provideHttpClient(withInterceptors([authInterceptor, tenantInterceptor, errorInterceptor])),
    provideAnimations(),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng',
          },
          darkModeSelector: false,
        },
      },
      zIndex: {
        modal: 1000000, // dialog, sidebar
        overlay: 999999, // dropdown, overlaypanel
        menu: 1000000, // overlay menus
        tooltip: 1000000, // tooltip
      },
      ripple: true,
      pt: {
        datePicker: {
          dropdown: 'bg-nutrix',
          dropdownIcon: 'text-white',
        },
        select: {
          clearIcon: 'text-nutrix',
        },
        multiSelect: {
          clearIcon: 'text-nutrix',
        },
        treeSelect: {
          clearIcon: 'text-nutrix',
        },
      },
    }),
    provideEchartsCore({
      echarts,
    }),
    MessageService,
    ConfirmationService,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
      const authService = inject(AuthService);
      return firstValueFrom(authService.fetchAndStoreCurrentUser()).catch(() => null);
    }),
  ],
};
